import { Button, Dialog, DialogActions, DialogContent, Stack } from "@mui/material";
import ClosableDialogTitle from "common/ClosableDialogTitle";
import * as Yup from "yup";
import { Field as FormikField, Formik, FormikHelpers } from "formik";
import { DateTime } from "luxon";
import PrsDatePickerField from "common/FormikFields/PrsDatePickerField";
import { TextField as FmuiTextField } from "formik-mui";
import { LoadingButton } from "@mui/lab";
import { Validations } from "../common/validations/common-yup-validations";
import { useMutation } from "@apollo/client";
import {
  AddCommitteeMeetingMutation,
  AvailableMeetingsQuery,
  CommitteeMeeting,
  CommitteeMeetingInput,
  FetchCommitteeMeetingsQuery
} from ".";
import { useNotifications } from "../notifications";
import { makeStyles } from "../makeStyles";
import _ from "lodash";
import { PracticeReview } from "../practice-reviews";
import { GetInasForUserQuery } from "../inas";
import { useCalendar } from "../util/CalendarProvider";
import { staticDataStyles } from "styles/common";
import { AppRoute } from "../Routes";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles()((theme) => ({
  ...staticDataStyles(theme)
}));

interface Props {
  handleClose: (prsToReassign?: PracticeReview[]) => void;
}

interface FormValues {
  meetingDate: DateTime | null;
  location: string;
  reviewQuota: number;
  subcommitteeDate: DateTime | null;
}

const AddMeetingDialog = (props: Props) => {
  const { classes } = useStyles();
  const notifications = useNotifications();
  const { configureForBusinessDaysMath } = useCalendar();
  const history = useHistory();

  const [addMeetingMutate, addMeetingMutation] = useMutation<
    { committeeMeeting: { add: CommitteeMeeting } },
    { meeting: CommitteeMeetingInput }
  >(AddCommitteeMeetingMutation, {
    refetchQueries: [
      { query: FetchCommitteeMeetingsQuery, variables: { excludeFuture: false, pastPeriod: null } },
      { query: GetInasForUserQuery },
      { query: AvailableMeetingsQuery }
    ]
  });

  async function addMeeting(values: FormValues, actions: FormikHelpers<FormValues>) {
    const meetingInput = getMeetingInputFromFormValues(values);

    const result = await addMeetingMutate({
      variables: { meeting: meetingInput }
    });

    actions.setSubmitting(false);
    props.handleClose();

    if (result.data?.committeeMeeting?.add.id) {
      notifications.success("Added meeting.");
      history.push(AppRoute.CommitteeMeeting.replace(":id", result.data.committeeMeeting.add.id.toString()));
    }
  }

  function getMeetingInputFromFormValues(values: FormValues): CommitteeMeetingInput {
    return {
      meetingDate: values.meetingDate!.toISODate(),
      location: values.location,
      reviewQuota: Number(values.reviewQuota),
      subcommitteeDate: values.subcommitteeDate?.toISODate() ?? null
    };
  }

  const validationSchema = Yup.object({
    meetingDate: Validations.requiredDate(),
    location: Validations.requiredText(),
    reviewQuota: Validations.requiredPositiveInteger()
  });

  const initialValues: FormValues = {
    meetingDate: null,
    location: "",
    reviewQuota: 30,
    subcommitteeDate: null
  };

  return (
    <Dialog open={true} fullWidth maxWidth="sm">
      <ClosableDialogTitle onClose={props.handleClose}>Add Committee Meeting</ClosableDialogTitle>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values, actions) => {
          await addMeeting(values, actions);

          props.handleClose();
        }}
        validationSchema={validationSchema}>
        {(formikProps) => (
          <>
            <DialogContent>
              <Stack spacing={3}>
                <FormikField
                  component={PrsDatePickerField}
                  name="meetingDate"
                  label="Date"
                  fullWidth
                  required
                  minDate={DateTime.local()}
                  onChange={(newDate: DateTime | null) => {
                    formikProps.setValues({
                      ...formikProps.values,
                      meetingDate: newDate,
                      subcommitteeDate: newDate ? configureForBusinessDaysMath(newDate).minusBusiness({ days: 2 }) : null
                    });
                  }}
                />
                <FormikField component={FmuiTextField} name="location" label="Location" fullWidth required />
                <FormikField component={FmuiTextField} name="reviewQuota" label="Maximum to Assign" fullWidth required />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => props.handleClose()} disabled={addMeetingMutation.loading}>
                Cancel
              </Button>
              <LoadingButton
                color="primary"
                variant="contained"
                loading={addMeetingMutation.loading}
                onClick={() => formikProps.submitForm()}>
                Add
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddMeetingDialog;
